/**
 * The cosmetic-progression **roll engine** (Cosmetic Progression Slice B, PRD #101
 * / ADR-0044, ADR-0048).
 *
 * Every 5 minutes of accrued online time a character unlocks one random
 * `{item, hue}` cosmetic. This module is the **sim-pure** core of that loop: given
 * a per-character `rollSeed`, the integer index of the boundary being awarded, the
 * character's body, and the list of valid hue ids, it produces a deterministic,
 * reproducible `{item, hue}` pair - and advances a whole {@link UnlockProgress}
 * across every boundary a flush crossed, appending each pair and auto-equipping it
 * into an empty layer.
 *
 * It reads **no clock and no asset** - the online-time tally and the valid-hue list
 * are handed in as plain data by the server - so it stays on the sim side of the
 * `architecture.test.ts` boundary (no `Date.now`, no `Math.random`, randomness only
 * through the seeded {@link SeededRng}), exactly like the rest of `sim/`.
 *
 * **Each character is its own slot machine.** The roll is seeded on the account's
 * persisted `rollSeed` *mixed with* the boundary's `unlockIndex`, never on the
 * index alone: seeding on the index alone would hand every same-body character the
 * identical sequence (a fixed ladder). Mixing the per-account seed in means two
 * characters with the same body and the same online time still diverge from their
 * very first pull, while each character's own sequence stays replayable across a
 * crash (the seed and index are both durable/derivable).
 */
import { SeededRng } from './rng';
import { equipment } from './content';
import { assembleEquipment } from './equipAssembly';
import type { EquippedItem } from './entity';

/**
 * How much accrued online time earns one unlock (Cosmetic Progression, PRD #101):
 * 5 minutes. The Nth boundary is crossed when `totalOnlineMs` reaches `N *
 * UNLOCK_INTERVAL_MS`, so the count of unlocks a character has *earned* is
 * `floor(totalOnlineMs / UNLOCK_INTERVAL_MS)` - the same `floor` the retroactive
 * grant spends over an already-banked tally. The server owns boundary detection at
 * the accrual seam (ADR-0040); the constant lives here so the boundary math and the
 * picker share one definition and stay unit-testable.
 */
export const UNLOCK_INTERVAL_MS = 5 * 60_000;

/**
 * How many times a single boundary's roll is re-attempted to avoid an exact
 * duplicate before giving up. Each attempt perturbs the derived seed (keeping the
 * picker pure), so a dup re-rolls to a *different* pair; the bound stops an
 * unwinnable retry loop once the collection has exhausted the body's item x hue
 * space (far beyond any realistic online time at POC scale). A boundary that can
 * find no fresh pair still counts as awarded - it just appends nothing.
 */
const MAX_ROLL_ATTEMPTS = 32;

/**
 * One rolled cosmetic: an atomic `{item, hue}` pair (ADR-0044). `item` is an
 * `equipment` registry id, `hue` a 1-based UO hue id drawn from the valid-hue list.
 * Structurally identical to the store's `UnlockedPair`, so a rolled pair drops
 * straight into `AccountStore.addUnlocked` with no mapping.
 */
export interface CosmeticRoll {
  /** `equipment` registry id of the rolled cosmetic, e.g. `hair.long`. */
  item: string;
  /** 1-based UO hue id the item rolled in (a distinct hue is a distinct unlock). */
  hue: number;
}

/**
 * A character's mutable cosmetic-progression state, the unit {@link advanceUnlocks}
 * advances. It is a plain data view the caller assembles over whichever backing
 * store owns the character: an `AccountRecord` for a reserved name, or an ephemeral
 * in-memory object for an anonymous session (PRD #101's "same loop in memory"). The
 * fields the advance reads and mutates:
 *
 * - `body`/`rollSeed` are fixed inputs (the character's UO body id and its
 *   persisted/ephemeral slot-machine seed);
 * - `totalOnlineMs` is the accrued tally the caller has already advanced;
 * - `unlocksGranted`, `unlocked`, and `equippedOutfit` are advanced **in place** -
 *   `unlocksGranted` is bumped to the earned boundary count, each fresh pair is
 *   appended to `unlocked`, and `equippedOutfit` is rebuilt with any empty layer a
 *   pair filled.
 */
export interface UnlockProgress {
  /** UO body id (400/401) the rolls are filtered to and the outfit assembles for. */
  body: number;
  /** The per-character slot-machine seed mixed with each `unlockIndex`. */
  rollSeed: number;
  /** Accrued online time in ms; the caller advances this before calling. */
  totalOnlineMs: number;
  /**
   * How many unlock boundaries have already been awarded to this character - the
   * highest `unlockIndex` granted. Advancing reconciles it up to
   * `floor(totalOnlineMs / UNLOCK_INTERVAL_MS)`, so this doubles as the retroactive
   * grant: a veteran first seen with `unlocksGranted: 0` and a large tally gets the
   * whole backlog in one pass.
   */
  unlocksGranted: number;
  /** The earned `{item, hue}` collection, appended to on each fresh unlock. */
  unlocked: CosmeticRoll[];
  /** The worn render stack; an unlock auto-equips only into a layer it leaves empty. */
  equippedOutfit: EquippedItem[];
  /**
   * The player's **auto-equip preference** (auto-equip toggle, ADR-0123): when a
   * fresh pair is unlocked, whether to auto-dress it into any empty layer. **Absent
   * ⇒ `true`** (the on-by-default checkbox), so a progress assembled without the
   * field keeps the original auto-equip behaviour unchanged. When explicitly
   * `false`, an unlock still appends to {@link unlocked} but leaves
   * {@link equippedOutfit} untouched - the player collects the item but keeps
   * whatever they are wearing until they equip it by hand from the wardrobe.
   */
  autoEquip?: boolean;
}

/**
 * The `equipment` registry ids valid for `body` (a 401 never offers a beard).
 * **System** layers the game assembles itself - the death shroud, `rollable: false` -
 * are excluded, so they are never rolled into a player's unlocks.
 */
function itemsForBody(body: number): string[] {
  return equipment
    .all()
    .filter((def) => def.rollable !== false && (!def.bodies || def.bodies.includes(body)))
    .map((def) => def.id);
}

/**
 * Mix the per-character `rollSeed`, the boundary `unlockIndex`, and the dup-retry
 * `attempt` into one 32-bit seed for a {@link SeededRng}. The mix is order- and
 * field-sensitive (each input folded through a distinct odd multiplier) so that:
 *
 * - different `rollSeed`s diverge from the very first index (the slot-machine
 *   property - same body/time, different drops);
 * - successive indices for one character walk an unpredictable sequence rather than
 *   a correlated ramp;
 * - a dup re-roll (`attempt + 1`) lands on an unrelated pair, not an adjacent one.
 */
function deriveSeed(rollSeed: number, unlockIndex: number, attempt: number): number {
  let h = rollSeed >>> 0;
  h = Math.imul(h ^ (unlockIndex + 0x9e3779b9), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13) ^ (attempt + 0xc2b2ae35), 0x27d4eb2f) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/**
 * The **sim-pure roll picker**: a pure function of `(rollSeed, unlockIndex, body,
 * validHues)` (plus a dup-retry `attempt`) yielding one `{item, hue}` pair. The
 * item is uniform over the `equipment` registry **filtered to `body`**; the hue is
 * uniform over `validHues` (the populated hue ids the server hands in), so the
 * returned hue is always a member of `validHues` - never an out-of-range id. Two
 * calls with the same arguments always return the same pair (determinism); two
 * different `rollSeed`s walk different sequences (divergence).
 *
 * Returns `null` only when there is nothing to draw from - an empty body item set
 * or an empty `validHues` - so the caller can skip rather than fabricate a pair.
 */
export function rollCosmetic(
  rollSeed: number,
  unlockIndex: number,
  body: number,
  validHues: readonly number[],
  attempt = 0,
): CosmeticRoll | null {
  const items = itemsForBody(body);
  if (items.length === 0 || validHues.length === 0) return null;
  const rng = new SeededRng(deriveSeed(rollSeed, unlockIndex, attempt));
  const item = items[rng.nextInt(items.length)]!;
  const hue = validHues[rng.nextInt(validHues.length)]!;
  return { item, hue };
}

/** Stable key for the exact-pair dedup (`item` + `hue` together are the unit). */
function pairKey(pair: CosmeticRoll): string {
  return `${pair.item} ${pair.hue}`;
}

/**
 * Roll one boundary's pair, re-rolling an **exact duplicate** (a pair already in
 * `owned`) up to {@link MAX_ROLL_ATTEMPTS} times by perturbing the attempt index.
 * Returns a fresh pair, or `null` if every attempt collided (or there was nothing
 * to draw) - the collection is effectively full, so the boundary awards nothing.
 */
function rollFreshPair(
  rollSeed: number,
  unlockIndex: number,
  body: number,
  validHues: readonly number[],
  owned: ReadonlySet<string>,
): CosmeticRoll | null {
  for (let attempt = 0; attempt < MAX_ROLL_ATTEMPTS; attempt++) {
    const pair = rollCosmetic(rollSeed, unlockIndex, body, validHues, attempt);
    if (pair === null) return null;
    if (!owned.has(pairKey(pair))) return pair;
  }
  return null;
}

/**
 * Auto-equip a freshly-unlocked pair into the outfit **only if its layer is empty**
 * (ADR-0044): your first hat lands on your head, but a later hat never silently
 * replaces the one you are wearing. Implemented by re-assembling the current outfit
 * with the new pair *appended* through {@link assembleEquipment}, whose
 * "first-per-layer wins" rule keeps any already-worn item and drops the newcomer
 * when its layer is occupied. Returns the (possibly unchanged) outfit in canonical
 * layer order.
 */
function autoEquip(outfit: EquippedItem[], pair: CosmeticRoll, body: number): EquippedItem[] {
  const requested = [
    ...outfit.map((item) => ({ id: item.id, hue: item.hue })),
    { id: pair.item, hue: pair.hue },
  ];
  return assembleEquipment(requested, body);
}

/**
 * Advance a character's {@link UnlockProgress} across **every** unlock boundary its
 * `totalOnlineMs` has now reached, mutating the progress in place and returning the
 * list of pairs unlocked this pass (in award order, for the caller's emote / log).
 *
 * For each boundary index `unlocksGranted + 1 .. floor(totalOnlineMs / interval)`:
 * roll a fresh (exact-dup-guarded) pair seeded on `(rollSeed, unlockIndex)`, append
 * it to `unlocked`, and - **unless `progress.autoEquip === false`** (the auto-equip
 * toggle, ADR-0123) - auto-equip it into any empty layer. With the preference off,
 * the pair is still collected but the worn outfit is left exactly as-is.
 * `unlocksGranted` is advanced for **every** boundary even when a roll finds no
 * fresh pair, so the loop
 * always terminates and never re-awards a boundary. The same call serves both the
 * live per-boundary unlock (a flush crossed one or a few boundaries) and the
 * one-batch retroactive grant (first sight of a veteran with `unlocksGranted: 0`).
 */
export function advanceUnlocks(
  progress: UnlockProgress,
  validHues: readonly number[],
): CosmeticRoll[] {
  const target = Math.floor(progress.totalOnlineMs / UNLOCK_INTERVAL_MS);
  if (target <= progress.unlocksGranted) return [];

  const owned = new Set(progress.unlocked.map(pairKey));
  const awarded: CosmeticRoll[] = [];

  for (let index = progress.unlocksGranted + 1; index <= target; index++) {
    const pair = rollFreshPair(progress.rollSeed, index, progress.body, validHues, owned);
    if (pair !== null) {
      owned.add(pairKey(pair));
      progress.unlocked.push(pair);
      // Auto-equip into an empty layer only when the preference is on (absent ⇒ on,
      // ADR-0123); off means collect-but-don't-wear until the player equips by hand.
      if (progress.autoEquip !== false) {
        progress.equippedOutfit = autoEquip(progress.equippedOutfit, pair, progress.body);
      }
      awarded.push(pair);
    }
  }
  progress.unlocksGranted = target;
  return awarded;
}
